import { StyledDailyBonusBadge } from '../style';
import { ActionButton } from '../../ActionButton';
import { EFontWeights } from 'utils/enums';
import { DailyBonusBadgeProps } from '../types';
import '../style.scss';
import { useEffect, useRef, useState } from 'react';

const TIME_LEFT_INTERVAL = 1000;

const getTimeLabel = (timeLeft: number): string => {
  const totalSeconds = Math.round(timeLeft / TIME_LEFT_INTERVAL);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const DailyBonusPopupBadge: React.FC<DailyBonusBadgeProps> = ({
  title,
  titleWeight = EFontWeights.Bold,
  titleSize = '24px',
  badgeCoverImage,
  borderColor,
  borderWidth,
  font,
  targetDate,
  onTargetDateReach,
  onClickBadge,
  offerId,
}) => {
  const [timeLeftLabel, setTimeLeftLabel] = useState<string>('');
  const [secondsLeft, setSecondsLeft] = useState<number>();

  const updateSecondsIntervalRef = useRef<any>();

  const updateRemainingTime = () => {
    if (!targetDate) return;
    const remainingTime = targetDate.getTime() - new Date().getTime();
    setSecondsLeft(remainingTime);

    return remainingTime;
  };

  useEffect(() => {
    if (!targetDate) return;

    const remainingTime = updateRemainingTime();

    if (remainingTime && remainingTime <= 0) {
      return;
    }

    updateSecondsIntervalRef.current = setInterval(
      () => setSecondsLeft((prev) => (prev ? prev - TIME_LEFT_INTERVAL : prev)),
      TIME_LEFT_INTERVAL,
    );
    return () => {
      if (updateSecondsIntervalRef.current)
        clearInterval(updateSecondsIntervalRef.current);
    };
  }, [targetDate]);

  useEffect(() => {
    if (secondsLeft === undefined) return;

    if (secondsLeft <= 0) {
      setTimeLeftLabel('');
      onTargetDateReach?.();

      if (updateSecondsIntervalRef.current)
        clearInterval(updateSecondsIntervalRef.current);
    } else {
      setTimeLeftLabel(getTimeLabel(secondsLeft));
    }
  }, [secondsLeft]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateRemainingTime();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [targetDate]);

  return (
    <StyledDailyBonusBadge
      titleWeight={titleWeight}
      titleSize={titleSize}
      badgeCoverImage={badgeCoverImage}
      borderWidth={borderWidth}
      borderColor={borderColor || { colorOne: '#ffffff' }}
    >
      <div
        className="daily-bonus-badge-banner"
        onClick={() => onClickBadge?.(offerId)}
      >
        <div className="daily-bonus-badge-banner__content">
          <h2 className="daily-bonus-badge-banner__title">{title}</h2>
          {timeLeftLabel && (
            <ActionButton
              className="daily-bonus-badge-banner__action-button"
              text={timeLeftLabel}
              borderRadius={'7px'}
              width={'56px'}
              textColor="black"
              font={font}
              fontWeight="600"
            />
          )}
        </div>
      </div>
    </StyledDailyBonusBadge>
  );
};
